import React from 'react';
import { useIndices } from '@/hooks/useIndices';
import { type IndexQuote } from '@/services/marketDataApi';
import { cn } from '@/lib/utils';

// ── Single ticker item ────────────────────────────────────────────────────
const TickerItem: React.FC<{ item: IndexQuote }> = ({ item }) => {
  const up = item.changePct >= 0;
  return (
    <div className="flex items-center gap-2.5 px-5 py-0 border-r border-white/10 last:border-r-0 shrink-0">
      <span className="text-[11px] font-semibold text-white/70 tracking-wide whitespace-nowrap uppercase">
        {item.name}
      </span>
      <span className="text-[11px] font-mono text-white whitespace-nowrap">
        {item.price.toLocaleString('en-IN', { maximumFractionDigits: 2 })}
      </span>
      <span
        className={cn(
          'text-[11px] font-mono font-semibold whitespace-nowrap flex items-center gap-0.5',
          up ? 'text-green-400' : 'text-red-400'
        )}
      >
        <span className="text-[9px]">{up ? '▲' : '▼'}</span>
        {up ? '+' : ''}{item.changePct.toFixed(2)}%
      </span>
    </div>
  );
};

// ── Skeleton ──────────────────────────────────────────────────────────────
const TickerSkeleton: React.FC = () => (
  <>
    {Array.from({ length: 6 }).map((_, i) => (
      <div key={i} className="flex items-center gap-2.5 px-5 border-r border-white/10 shrink-0">
        <div className="h-2 w-16 bg-white/10 rounded animate-pulse" />
        <div className="h-2 w-14 bg-white/10 rounded animate-pulse" />
        <div className="h-2 w-10 bg-white/10 rounded animate-pulse" />
      </div>
    ))}
  </>
);

// ── Main ticker ───────────────────────────────────────────────────────────
const MarketTicker: React.FC = () => {
  const { indices, loading, error } = useIndices();

  const items = !loading && indices.length > 0 ? [...indices, ...indices] : null;

  return (
    <div
      className="fixed bottom-0 left-0 right-0 z-40 h-10 overflow-hidden border-t border-white/10 bg-navy-950"
      role="region"
      aria-label="Live Indian market indices"
    >
      {/* Fade edges */}
      <div className="pointer-events-none absolute left-0 top-0 bottom-0 w-10 z-10"
        style={{ background: 'linear-gradient(to right, #020817, transparent)' }} />
      <div className="pointer-events-none absolute right-0 top-0 bottom-0 w-16 z-10"
        style={{ background: 'linear-gradient(to left, #020817, transparent)' }} />

      <div className="flex items-center h-full">
        {loading ? (
          <div className="flex h-full items-center"><TickerSkeleton /></div>
        ) : error ? (
          <span className="px-6 text-[11px] text-white/40">Market data temporarily unavailable</span>
        ) : (
          <div
            className="flex items-center h-full"
            style={{
              animation: 'ticker-scroll 36s linear infinite',
              width: 'max-content',
            }}
          >
            {items?.map((item, i) => (
              <TickerItem key={`${item.symbol}-${i}`} item={item} />
            ))}
          </div>
        )}
      </div>

      {/* Live pulse */}
      {!loading && !error && (
        <div className="absolute right-14 top-1/2 -translate-y-1/2 z-20 flex items-center gap-1.5">
          <span className="relative flex h-1.5 w-1.5">
            <span className="animate-ping absolute inline-flex h-full w-full rounded-full bg-green-400 opacity-75" />
            <span className="relative inline-flex rounded-full h-1.5 w-1.5 bg-green-400" />
          </span>
          <span className="text-[10px] text-white/50 font-medium hidden sm:block tracking-widest uppercase">Live</span>
        </div>
      )}
    </div>
  );
};

export default MarketTicker;
